import { DateTime, Duration } from 'luxon';
import { useStore } from 'vuex';

export function useDateTime() {
	const store = useStore();

	const formatToSeconds = (time, formatter) => {
		if (time === null || time === undefined) {
			return 0;
		}

		if (typeof time === 'number') {
			return time;
		}

		const timeStr = String(time);

		if (timeStr.indexOf(':') !== -1) {
			const splitTime = timeStr.split(':');
			if (splitTime.length === 2) {
				if (splitTime[1].length !== 2) {
					return 0;
				}
				const hours = splitTime[0].length < 2 ? '0' + splitTime[0] : splitTime[0];
				const minutes = splitTime[1];
				return Math.round(formatter(hours, minutes));
			}
		}
		return 0;
	};

	const formatSecondsDuration = (seconds) => {
		if (seconds === null || seconds === undefined || seconds === '') {
			// Not defined
			return undefined;
		}
		const duration = Duration.fromObject({ seconds: seconds });
		if (!duration.isValid) {
			// Invalid
			return undefined;
		}
		if (seconds < 0) {
			const time = duration.negate().toFormat('hh:mm');
			return time === '00:00' ? time : `-${time}`;
		}
		return duration.toFormat('hh:mm');
	};

	const formatSecondsDurationToHM = (seconds) => {
		if (seconds === null || seconds === undefined || seconds === '') {
			return undefined;
		}
		const duration = Duration.fromObject({ seconds: Math.abs(seconds) });
		if (!duration.isValid) {
			return undefined;
		}

		const shifted = duration.shiftTo('hours', 'minutes');
		const hours = Math.trunc(shifted.hours);
		const minutes = Math.round(shifted.minutes);

		const formatted = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;

		if (seconds < 0) {
			return formatted === '0m' ? formatted : `-${formatted}`;
		}
		return formatted;
	};

	const formatDateTime = (value) => {
		if (value) {
			return DateTime.fromISO(String(value)).toFormat(store._state.data.profile.locale.dateTimeFormat);
		}

		return '';
	};

	const formatTime = (value) => {
		if (value) {
			return DateTime.fromISO(String(value)).toFormat(store._state.data.profile.locale.timeFormat);
		}

		return '';
	};

	const formatDate = (value) => {
		if (value) {
			return DateTime.fromISO(String(value)).toFormat(store._state.data.profile.locale.dateFormat);
		}

		return '';
	};

	const formatDateUTC = (value) => {
		if (value) {
			return DateTime.fromISO(String(value), { zone: 'utc' }).toFormat(store._state.data.profile.locale.dateFormat);
		}

		return '';
	};

	const formatTimeToSeconds = (time) => {
		return formatToSeconds(time, (hours, minutes) => Duration.fromISOTime(hours + ':' + minutes).toMillis() / 1000);
	};

	const formatDurationToSeconds = (time) => {
		return formatToSeconds(time, (hours, minutes) => hours * 3600 + minutes * 60);
	};

	/**
	 * Applies the planning timezone to a Luxon DateTime object.
	 * @param {import('luxon').DateTime} dt - The Luxon DateTime object.
	 * @returns {import('luxon').DateTime} A new DateTime object in the planning timezone.
	 */
	const applyPlanningTimezone = (dt) => {
		return dt.setZone(store._state.data.setup.planning_timezone);
	};

	const formatSecondsDurationWithSeconds = (seconds) => {
		if (seconds === null || seconds === undefined) {
			// Not defined
			return undefined;
		}
		if (isNaN(seconds)) {
			// No number
			return undefined;
		}
		const duration = Duration.fromObject({ seconds });
		if (seconds < 0) {
			const time = duration.negate().toFormat('hh:mm:ss');
			return time === '00:00:00' ? time : `-${time}`;
		}
		return duration.toFormat('hh:mm:ss');
	};

	const decimal2 = (value) => {
		if (value === undefined || value === null) {
			// not defined
			return null;
		}
		if (isNaN(value)) {
			// No number
			return null;
		}
		return Math.round(value * 100) / 100;
	};

	const decimal = (value) => {
		if (value === undefined || value === null) {
			// not defined
			return null;
		}
		if (isNaN(value)) {
			// No number
			return null;
		}
		return Math.round(value);
	};

	return {
		formatSecondsDuration,
		formatDateTime,
		formatDate,
		formatDateUTC,
		formatTime,
		applyPlanningTimezone,
		formatSecondsDurationWithSeconds,
		decimal2,
		decimal,
		formatTimeToSeconds,
		formatDurationToSeconds,
		formatSecondsDurationToHM,
	};
}

export default useDateTime;
